import React, { useEffect, useState } from "react";
import { Form, Space, Select, DatePicker } from "antd";
import { DeleteOutlined, EditOutlined, MailOutlined } from "@ant-design/icons";
import DataTable from "../../components/Comman/CustomTable/CommonTable";
import { useDispatch, useSelector } from "react-redux";
import organizatonLogo from "../../../src/images/icon/organization.png";
import SideDrawer from "../../components/Comman/Drawer/SideDrawer";
import {
  submissionListAction,
  UserOrganizationListAction,
} from "../../actions/user";
import {
  formatBackendDateToCST,
  formatDateTimeToCST,
  formatSubmissionValue,
  getIntervalNameById,
} from "../../utils/comman";
import type { DatePickerProps } from "antd";
import Button from "../../components/Button/Button";
import dayjs from "dayjs";

const DataReviewListing = () => {
  const [form] = Form.useForm();
  const { Option } = Select;
  const dispatch = useDispatch<any>();
  const [data, setData] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [filter, setFilter] = useState<boolean>(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const user = useSelector((state: any) => state?.Auth?.user);
  const roleId = user?.user?.roleId;
  const [isDataEntryModelOpen, setIsDataEntryModelOpen] =
    useState<boolean>(false);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);

  const [sortConfig, setSortConfig] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({
    field: null,
    order: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [orgPagination, setOrgPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const handleRefresh = useSelector(
    (state: any) => state?.General?.updateListing
  );

  // Fetches the list of organizations with optional query parameters
  const fetchSubmissionList = (params = {}) => {
    setLoading(true); // Show loading indicator before making the API call
    const requestParams = {
      params,
      id: roleId,
      startDate,
      endDate,
      organizationId: selectedOrgId,
    };
    dispatch(
      submissionListAction(requestParams, (resp: any) => {
        setLoading(false); // Hide loading indicator once the API call is completed
        if (resp?.status === 200) {
          setData(resp?.data || []); // Store the fetched organization data

          // Update pagination state based on API response
          setPagination({
            ...pagination,
            total: resp.pagination?.totalItems,
            current: resp.pagination?.currentPage,
            pageSize: resp.pagination?.pageSize,
          });
        }
      })
    );
  };

  // useEffect to trigger API call when pagination state changes
  useEffect(() => {
    fetchSubmissionList({
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
    });
  }, [handleRefresh, filter]); // Dependency array ensures function re-runs on pagination updates

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    let newSortConfig = { ...sortConfig };
    if (sorter && Object.keys(sorter).length > 0) {
      newSortConfig = {
        field: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      };
    }
    setSortConfig(newSortConfig);
    fetchSubmissionList({
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
      ...(newSortConfig.field && newSortConfig.order
        ? {
            sort: newSortConfig.field,
            order: newSortConfig.order,
          }
        : {}),
    });
  };

  const handleIsDataEntryModelOpen: any = () => {
    setIsDataEntryModelOpen(!isDataEntryModelOpen);
  };

  const handleEditClick = (record: any) => {
    handleIsDataEntryModelOpen();
    setSelectedEntry(record); // Store the selected organization's data
  };

  const fetchOrganizationList = (params = {}) => {
    setLoading(true);
    const requestParams = {
      ...params,
      ...(sortConfig.field && sortConfig.order
        ? {
            sort: sortConfig.field,
            order: sortConfig.order,
          }
        : {}),
    };
    dispatch(
      UserOrganizationListAction(requestParams, (resp: any) => {
        setLoading(false);
        if (resp?.status === 200) {
          setOrganizations(resp.data || []);
          setOrgPagination({
            ...orgPagination,
            total: resp.pagination?.totalItems,
            current: resp.pagination?.currentPage,
            pageSize: resp.pagination?.pageSize,
          });
        }
      })
    );
  };

  useEffect(() => {
    if (roleId != 1)
      fetchOrganizationList({
        page: orgPagination.current || 1,
        limit: orgPagination.pageSize || 10,
      });
  }, [handleRefresh]);

  // Handle onPopupScroll event for the Select component
  const handleSelectScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Check if user has scrolled to the bottom
    const { target } = e;
    if (
      !loading &&
      target instanceof HTMLElement &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 20 &&
      orgPagination.current < orgPagination.totalPages
    ) {
      // Load next page
      const nextPage = orgPagination.current + 1;
      fetchOrganizationList({
        page: nextPage,
        limit: orgPagination.pageSize,
      });
    }
  };

  //Date picker
  const onChangeStartDate: DatePickerProps["onChange"] = (date, dateString) => {
    setStartDate(dateString);
  };

  // Disable future dates
  const disableFutureDates = (current: dayjs.Dayjs) => {
    return current && current > dayjs().endOf("day");
  };

  const onChangeEndDate: DatePickerProps["onChange"] = (date, dateString) => {
    setEndDate(dateString);
  };

  const handleFilterButton = () => {
    setFilter(!filter);
  };

  const columns = [
    {
      title: "Data Element",
      dataIndex: "dataElement",
      key: "dataElement.id",
      render: (dataElement: any) => dataElement?.DataElements_Name,
    },
    {
      title: "Data User Name",
      dataIndex: "data_user",
      key: "updatedBy",
      render: (_, data) => data?.data_user?.name || "-",
      sorter: true,
    },
    {
      title: "Data User Email",
      dataIndex: "updatedBy",
      key: "updatedBy",
      render: (_, data) => data?.updatedBy?.email || "-",
    },
    {
      title: "Org",
      dataIndex: "organization",
      key: "organization",
      render: (_, data) => data?.organization?.shortCode || "-",
      sorter: true,
    },

    {
      title: "Submission Data Year",
      dataIndex: "SubmissionData_Year",
      key: "SubmissionData_Year",
    },
    {
      title: "Submission Data Period",
      dataIndex: "SubmissionData_Period",
      key: "SubmissionData_Period",
      // render: (value) => getIntervalNameById(value),
      render: (_, record) =>
        record.SubmissionData_Period
          ? `${record.SubmissionData_IntervalType} ${
              record.SubmissionData_Period || "N/A"
            }`
          : "-",
    },
    {
      title: "Submission Data Value",
      dataIndex: "SubmissionData_Value",
      key: "SubmissionData_Value",
      render: (value: string, record: any) => (
        <div className="text-right">
          {formatSubmissionValue(
            value,
            record?.dataElement?.DataElements_DataType
          )}
        </div>
      ),
    },
    // {
    //   title: "Interval Type",
    //   dataIndex: "SubmissionData_IntervalType",
    //   key: "SubmissionData_IntervalType",
    // },
    {
      title: "Submission Due On",
      dataIndex: "SubmissionData_CalculatedDueDate",
      key: "SubmissionData_CalculatedDueDate",
      render: (value) => formatBackendDateToCST(value),
    },
    {
      title: "Action",
      key: "action",
      render: (record: any) => (
        <Space size="middle">
          <span
            style={{ color: "#3DA57F", fontWeight: "bold", fontSize: "20px" }}
          >
            <EditOutlined
              onClick={() => {
                handleEditClick(record);
              }}
            />
          </span>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* <Header /> */}
      <h1 className="text-3xl font-bold text-left mb-6 text-white">
        Data Review
      </h1>
      {[2, 3].includes(roleId) && (
        <Form
          form={form}
          layout="vertical"
          // onValuesChange={handleFormValuesChange}
        >
          <div className="flex justify-between bg-primary p-[15px] rounded-[10px] items-center">
            <div className="flex gap-[45px] items-center">
              <div className="select-col flex gap-[10px] items-center">
                <p className="info-col">Select Organization</p>
                {/* <div className="ant-picker ant-picker-outlined css-dev-only-do-not-override-240cud range-datepicker-col"> */}
                <div className="mb-none">
                  <Form.Item
                    name="organizationId"
                    rules={[
                      {
                        required: true,
                        message: "Please select a organization",
                      },
                    ]}
                  >
                    <Select
                      // open={true}
                      placeholder="Select Organization"
                      className="input-main-col focus:bg-transparent rounded-[5px] mb-[0px] p-[10px] border-1 border-transparent focus:border-white w-full bg-[#013351] placeholder-[#005A90] color-[#fff] h-auto"
                      onPopupScroll={handleSelectScroll}
                      loading={loading}
                      notFoundContent={loading ? "Loading..." : "No data found"}
                      listHeight={200}
                      allowClear
                      showSearch
                      onChange={(value) => setSelectedOrgId(value)}
                    >
                      {organizations?.map((org) => (
                        <Option
                          key={org?.id}
                          value={org?.id}
                          style={{
                            backgroundColor: "#013351",
                            border: "none",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: "2px",
                              width: "auto",
                            }}
                          >
                            <img
                              src={org?.logo ? org?.logo : organizatonLogo}
                              alt={org?.name}
                              style={{
                                width: 28,
                                marginRight: 5,
                                objectFit: "cover",
                                borderRadius: "1px",
                              }}
                            />
                            {/* <Avatar
                            src={org?.logo ? org?.logo : undefined} // Dynamically set image
                            icon={!org?.logo && organizatonLogo} // Show icon if no image
                            className="p-[3px] bg-primary rounded-[10px] object-cover"
                            style={{
                              // width: 28,
                              marginRight: 5,
                              objectFit: "contain",
                              borderRadius: "1px",
                            }}
                          /> */}

                            {org?.name}
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
              <div className="select-col flex gap-[10px] items-center">
                <p className="info-col">
                  Select Date Range (Submission Date) :
                </p>
                <div className="ant-picker ant-picker-outlined css-dev-only-do-not-override-240cud range-datepicker-col flex gap-5">
                  <div>
                    <DatePicker
                      onChange={onChangeStartDate}
                      className="review-custom-datepicker-input"
                      disabledDate={disableFutureDates}
                    />
                  </div>
                  <div>
                    <DatePicker
                      onChange={onChangeEndDate}
                      className="review-custom-datepicker-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Button
                buttonText="Filter"
                width="140px"
                onClick={handleFilterButton}
              ></Button>
            </div>
          </div>
        </Form>
      )}
      <div className="table-col">
        <DataTable
          columns={columns}
          data={data}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          onTableChange={handleTableChange}
        />
      </div>
      <SideDrawer
        open={isDataEntryModelOpen}
        onClose={handleIsDataEntryModelOpen}
        type={"dataEntry"}
        organizationData={selectedEntry} // Pass only selected record
      />
    </div>
  );
};

export default DataReviewListing;
